import { pickLocalizedTitle } from './localizedContent';
import type { AppLocale } from '../i18n';

/** Splits course / level / lesson path segments from API subtitles. */
export const FLOW_CONTEXT_PATH_SPLIT = /\s*(?:›|‹|»|«|>|<|→)\s*/g;

/** Course title from flow item subtitle (first segment before ›). */
export function courseTitleFromFlowContext(
  contextSubtitle: string,
  courseId: number,
  contextSubtitleFa?: string | null,
  locale: AppLocale = 'en'
): string {
  const subtitle =
    locale === 'fa' && contextSubtitleFa?.trim() ? contextSubtitleFa : contextSubtitle;
  const parts = subtitle.split(FLOW_CONTEXT_PATH_SPLIT);
  const first = parts[0]?.trim();
  return first || `Course ${courseId}`;
}

/**
 * Breadcrumb path under quiz/exercise rows.
 * In RTL: keep course › level › lesson order; `dir=rtl` places course on the right; swap › to ‹.
 */
export function formatFlowContextSubtitle(
  contextSubtitle: string,
  isRtl: boolean,
  contextSubtitleFa?: string | null
): string {
  const primary =
    isRtl && contextSubtitleFa?.trim() ? contextSubtitleFa.trim() : contextSubtitle;
  const trimmed = primary.trim();
  if (!trimmed) return contextSubtitle;
  if (!isRtl) return trimmed;
  return trimmed.replace(FLOW_CONTEXT_PATH_SPLIT, ' < ');
}

/** Course › level › lesson segments for breadcrumb rendering. */
export function splitFlowContextParts(
  contextSubtitle: string,
  isRtl: boolean,
  contextSubtitleFa?: string | null
): string[] {
  const primary =
    isRtl && contextSubtitleFa?.trim() ? contextSubtitleFa.trim() : contextSubtitle;
  const trimmed = primary.trim();
  const source = trimmed || contextSubtitle.trim();
  if (!source) return [];
  return source.split(FLOW_CONTEXT_PATH_SPLIT).map((p) => p.trim()).filter(Boolean);
}

export type CatalogFlowCourseOption = { courseId: number; title: string };

export function buildCatalogFlowCourseOptions(
  items: { courseId: number; contextSubtitle: string; contextSubtitleFa?: string | null }[],
  locale: AppLocale = 'en'
): CatalogFlowCourseOption[] {
  const map = new Map<number, string>();
  for (const item of items) {
    if (item.courseId <= 0) continue;
    if (!map.has(item.courseId)) {
      map.set(
        item.courseId,
        courseTitleFromFlowContext(item.contextSubtitle, item.courseId, item.contextSubtitleFa, locale)
      );
    }
  }
  return Array.from(map.entries())
    .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }))
    .map(([courseId, title]) => ({ courseId, title }));
}

/** Localized quiz/exercise title on catalog flow rows. */
export function pickFlowItemTitle(
  item: { title: string; titleFa?: string | null },
  locale: AppLocale
): string {
  return pickLocalizedTitle(item, locale);
}
